import React, { useEffect, useRef, useState } from "react";
import { Empty, Tooltip } from "antd";
import { InfoCircleOutlined, FileExcelOutlined } from "@ant-design/icons";
import * as XLSXStyle from "xlsx-js-style";
import axios from "axios";
import moment from "moment";
import { SegmentedItem, ButtonItem, MensajeError } from "../../../../../Components/Items";
import EstadosLeyenda from "../../../../../Components/EstadosLeyenda";
import {
  construirTasksGantt,
  construirFilasExportGantt,
} from "./construirTasksGantt";
import "./ganttPresupuesto.css";

// dhtmlx-gantt se carga vía <script> clásico en public/index.html (no import de ES) —
// ver el comentario ahí para el porqué. El script es bloqueante y se ejecuta antes que
// el bundle de la app, así que window.dhtmlxgantt ya existe en este punto.
const gantt = window.dhtmlxgantt.gantt;

const NIVELES_ZOOM = [
  {
    name: "Día",
    scale_height: 27,
    min_column_width: 40,
    scales: [{ unit: "day", step: 1, format: "%d %M" }],
  },
  {
    name: "Semana",
    scale_height: 50,
    min_column_width: 60,
    scales: [
      { unit: "week", step: 1, format: "Semana %W" },
      { unit: "day", step: 1, format: "%D" },
    ],
  },
  {
    name: "Mes",
    scale_height: 50,
    min_column_width: 70,
    scales: [
      { unit: "month", step: 1, format: "%F %Y" },
      { unit: "week", step: 1, format: "Sem %W" },
    ],
  },
];

const ZOOM_DEFAULT = "Semana";
const ZOOM_OPCIONES = [
  { label: "Día", value: "Día" },
  { label: "Semana", value: "Semana" },
  { label: "Mes", value: "Mes" },
];

const COLOR_TAREA = "#00b5e2";
const COLOR_GRUPO = "#fac465";

const LEYENDA_BARRAS = [
  { color: COLOR_TAREA, label: "Partida", key: "partida" },
  {
    color: COLOR_GRUPO,
    label: "Grupo / resumen (suma de sus partidas)",
    key: "grupo",
  },
];

const colorAvance = (avance) => {
  if (avance < 30) return "#e64848";
  if (avance < 70) return "#f5a623";
  return "#52c41a";
};

// Reemplaza el tab "Diagrama de gantt" (antes gantt-task-react, sin persistencia real y
// con progress fijo en 0). Fechas editables en cualquier momento del ciclo de vida del
// presupuesto (start/end son metadata de calendario, exentas del candado de
// inmutabilidad — ver PresupuestoPU.js en el backend); el % de avance se deriva de
// producción real y nunca se arrastra a mano.
function GanttPresupuesto({
  partidas,
  todasMatrices,
  presupuestoId,
  empresaId,
  headersToken,
  calcularConImpuestos,
  nombrePresupuesto,
}) {
  const containerRef = useRef(null);
  const inicializado = useRef(false);
  const datosArrastreOriginal = useRef(null);
  const propsRef = useRef({ presupuestoId, empresaId, headersToken });
  propsRef.current = { presupuestoId, empresaId, headersToken };
  const [zoom, setZoom] = useState(ZOOM_DEFAULT);

  useEffect(() => {
    if (inicializado.current) return;
    inicializado.current = true;

    gantt.i18n.setLocale("es_mx");

    // Sin edición vía popup nativo, solo arrastrando la barra
    gantt.config.details_on_dblclick = false;
    // Sin dependencias entre partidas por ahora
    gantt.config.drag_links = false;

    gantt.config.date_format = "%Y-%m-%d %H:%i";
    gantt.config.drag_progress = false; // el avance se deriva de producción, no se arrastra
    gantt.config.autosize = false;
    gantt.config.columns = [
      { name: "text", label: "Partida", tree: true, width: 280, resize: true },
      {
        name: "start_date",
        label: "Inicio",
        align: "center",
        width: 90,
        resize: true,
        template: (task) => moment(task.start_date).format("DD/MM/YYYY"),
      },
      {
        name: "duration",
        label: "Días",
        align: "center",
        width: 55,
        resize: true,
      },
      {
        name: "avance",
        label: "Avance",
        align: "center",
        width: 140,
        resize: true,
        template: (task) => {
          const porcentaje = Math.round((task.progress || 0) * 100);
          return (
            `<div class="gantt-avance-col">` +
            `<span class="gantt-avance-barra" style="width:${porcentaje}%;background:${colorAvance(porcentaje)}"></span>` +
            `<span class="gantt-avance-texto">${porcentaje}%</span>` +
            `</div>`
          );
        },
      },
    ];

    // Colores propios de Biwo en vez del skin azul/verde por defecto de dhtmlx.
    gantt.templates.task_class = (start, end, task) =>
      task.type === "project" ? "gantt-biwo-project" : "gantt-biwo-task";

    gantt.templates.tooltip_text = (start, end, task) => {
      const porcentaje = Math.round((task.progress || 0) * 100);
      return (
        `<b>${task.text}</b><br/>` +
        `${moment(start).format("DD/MM/YYYY")} — ${moment(end).format("DD/MM/YYYY")}<br/>` +
        `Avance: ${porcentaje}%`
      );
    };

    gantt.ext.zoom.init({ levels: NIVELES_ZOOM });
    gantt.ext.zoom.setLevel(ZOOM_DEFAULT);

    gantt.attachEvent("onBeforeTaskDrag", (id) => {
      const task = gantt.getTask(id);
      datosArrastreOriginal.current = {
        id,
        start_date: new Date(task.start_date),
        end_date: new Date(task.end_date),
      };
      return true;
    });

    gantt.attachEvent("onAfterTaskDrag", (id, mode) => {
      if (mode === "progress") return; // drag_progress está deshabilitado, no debería pasar
      manejarDragTerminado(id);
    });

    gantt.init(containerRef.current);

    return () => {
      gantt.clearAll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const tasksData = construirTasksGantt(
      partidas,
      todasMatrices,
      calcularConImpuestos,
    );
    gantt.clearAll();
    gantt.parse(tasksData);
  }, [partidas, todasMatrices, calcularConImpuestos]);

  const recalcularAncestros = (idArrastrado) => {
    const cambios = [];
    const taskArrastrada = gantt.getTask(idArrastrado);
    cambios.push({
      path: taskArrastrada._path,
      start: taskArrastrada.start_date,
      end: taskArrastrada.end_date,
    });

    let idActual = idArrastrado;
    let idPadre = gantt.getParent(idActual);
    while (idPadre) {
      const padre = gantt.getTask(idPadre);
      if (padre.type !== "project") break;

      const hijos = gantt.getChildren(idPadre).map((hid) => gantt.getTask(hid));
      const nuevoInicio = new Date(
        Math.min(...hijos.map((h) => h.start_date.getTime())),
      );
      const nuevoFin = new Date(
        Math.max(...hijos.map((h) => h.end_date.getTime())),
      );

      padre.start_date = nuevoInicio;
      padre.end_date = nuevoFin;
      gantt.updateTask(idPadre);

      cambios.push({ path: padre._path, start: nuevoInicio, end: nuevoFin });
      idActual = idPadre;
      idPadre = gantt.getParent(idActual);
    }

    return cambios;
  };

  const manejarDragTerminado = async (id) => {
    const cambios = recalcularAncestros(id);
    const {
      presupuestoId: pId,
      empresaId: eId,
      headersToken: hToken,
    } = propsRef.current;

    try {
      const res = await axios.post(
        "presupuestosPU/actualizarFechaPartida",
        {
          presupuestoId: pId,
          empresaId: eId,
          cambios: cambios.map((c) => ({
            path: c.path,
            start: c.start,
            end: c.end,
          })),
        },
        { headers: hToken },
      );
      if (res.data.success !== 2) {
        MensajeError({
          message: res.data.message || "No se pudo guardar el cambio de fecha.",
        });
        revertirDrag();
      }
    } catch (err) {
      MensajeError({ message: "No se pudo guardar el cambio de fecha." });
      revertirDrag();
    }
  };

  const revertirDrag = () => {
    if (!datosArrastreOriginal.current) return;
    const { id, start_date, end_date } = datosArrastreOriginal.current;
    const task = gantt.getTask(id);
    if (task) {
      task.start_date = start_date;
      task.end_date = end_date;
      gantt.updateTask(id);
      gantt.render();
    }
  };

  // Export local, sin depender del servicio en la nube de dhtmlx
  const exportarExcel = () => {
    const filas = construirFilasExportGantt(
      partidas,
      todasMatrices,
      calcularConImpuestos,
    );

    const estiloEncabezado = {
      font: { bold: true },
      fill: { fgColor: { rgb: "F0F0F0" } },
      alignment: { horizontal: "center" },
    };
    const encabezado = [
      "Partida",
      "Tipo",
      "Inicio",
      "Fin",
      "Días",
      "Avance %",
    ].map((texto) => ({
      v: texto,
      t: "s",
      s: estiloEncabezado,
    }));

    const filasHoja = filas.map((fila) => [
      { v: fila.nombre, t: "s", s: { alignment: { indent: fila.nivel } } },
      {
        v: fila.esMatriz ? "Matriz" : fila.esGrupo ? "Grupo" : "Partida",
        t: "s",
        s: { alignment: { horizontal: "center" } },
      },
      {
        v: moment(fila.start).format("DD/MM/YYYY"),
        t: "s",
        s: { alignment: { horizontal: "center" } },
      },
      {
        v: moment(fila.end).format("DD/MM/YYYY"),
        t: "s",
        s: { alignment: { horizontal: "center" } },
      },
      { v: fila.dias, t: "n", s: { alignment: { horizontal: "center" } } },
      { v: fila.avance, t: "n", s: { alignment: { horizontal: "center" } } },
    ]);

    const worksheet = XLSXStyle.utils.aoa_to_sheet([encabezado, ...filasHoja]);
    worksheet["!cols"] = [
      { wpx: 280 },
      { wpx: 70 },
      { wpx: 90 },
      { wpx: 90 },
      { wpx: 55 },
      { wpx: 80 },
    ];

    const workbook = XLSXStyle.utils.book_new();
    XLSXStyle.utils.book_append_sheet(workbook, worksheet, "Gantt");
    XLSXStyle.writeFile(
      workbook,
      `Diagrama de gantt ${nombrePresupuesto || ""}.xlsx`,
    );
  };

  const hayPartidas = (partidas || []).length > 0;

  return (
    <div className="gantt-presupuesto-wrapper">
      <div className="gantt-presupuesto-toolbar">
        <div className="gantt-presupuesto-toolbar-izquierda">
          <Tooltip
            title={
              <div>
                Arrastra el centro de una barra para mover sus fechas, o su
                borde para cambiar la duración — se guarda solo, no hace falta
                darle a "Guardar".
                <br />
                Usa Día / Semana / Mes para acercar o alejar la línea de tiempo.
                <br />
                El % de Avance se calcula solo, con lo que ya se ha producido de
                cada partida; no se puede editar a mano.
              </div>
            }
          >
            <span className="gantt-presupuesto-ayuda">
              <InfoCircleOutlined /> Cómo usar el diagrama
            </span>
          </Tooltip>
          <EstadosLeyenda estados={LEYENDA_BARRAS} compact />
        </div>
        <div className="gantt-presupuesto-toolbar-derecha">
          <ButtonItem
            type="primary"
            className="botonAgregar"
            verde
            icon={<FileExcelOutlined />}
            onClick={exportarExcel}
          >
            Exportar a Excel
          </ButtonItem>
          <SegmentedItem
            options={ZOOM_OPCIONES}
            value={zoom}
            onChange={(value) => {
              setZoom(value);
              gantt.ext.zoom.setLevel(value);
            }}
          />
        </div>
      </div>
      {!hayPartidas && (
        <Empty
          description="No hay partidas para mostrar en el diagrama"
          style={{ padding: 40 }}
        />
      )}
      {/* Se mantiene siempre montado (solo oculto con CSS) para que la referencia del
          contenedor nunca sea null cuando dhtmlx-gantt se inicializa. */}
      <div
        ref={containerRef}
        className="gantt-presupuesto-container"
        style={{ display: hayPartidas ? "block" : "none" }}
      />
    </div>
  );
}

export default GanttPresupuesto;
